<template>
  <div>
    <div class="block">
      <div class="block-header">
        <h4 class="block-title">Admin Accounts</h4>
        <div class="block-header-options">
          <button
            type="button"
            class="header__option header__option-add"
            @click="$emit('addTenantUser')"
          >
            <span>&#x0002B;</span>
          </button>
        </div>
      </div>
      <div class="block-content pb-20">
        <div v-if="isLoading" class="block-content-loading">
          <content-loader
            :height="90"
            :width="400"
            :speed="2"
            primaryColor="#e0e0e0"
            secondaryColor="#bebebe"
          >
            <rect x="0" y="10" rx="7" ry="7" width="100%" height="0.875rem" />
            <rect x="0" y="38" rx="7" ry="7" width="100%" height="0.875rem" />
            <rect x="0" y="66" rx="7" ry="7" width="100%" height="0.875rem" />
          </content-loader>
        </div>
        <div v-if="!isLoading && !!!users.length">
          No accounts found.
        </div>
        <ul v-if="!isLoading && !!users.length" class="user">
          <UserListItem
            v-for="user in users"
            :user="user"
            :key="user.id"
            :handleUserEdit="() => $emit('editTenantUser', { user, tenant })"
            :handleUserView="handleUserView"
            :handleUserDelete="handleUserDelete"
            :handleUserRestore="handleUserRestore"
            :handleUserMore="() => $emit('tenantUserMore', { user, tenant })"
          />
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { ContentLoader } from "vue-content-loader";
import UserListItem from "@/modules/superadmin/elements/UserListItem";
import axios from "@/helpers/base";
import * as logger from "@/helpers/logger";

export default {
  name: "UserList",
  components: {
    ContentLoader,
    UserListItem
  },
  props: {
    tenant: {
      type: Object,
      default: () => {}
    },
    roles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isLoading: true,
      adminRole: null,
      users: []
    };
  },
  watch: {
    tenant(nextTenant) {
      this.isLoading = true;
      this.handleFetchTenantUsers(this.adminRole.id, nextTenant.id)
        .then(users => {
          this.users = users;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    roles(nextRoles) {
      if (nextRoles.length) {
        this.adminRole = nextRoles.find(role => role.value === 8);
      }
    }
  },
  async mounted() {
    try {
      this.setAdminRole();
      this.users = await this.handleFetchTenantUsers(
        this.adminRole.id,
        this.tenant.id
      );
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
    }

    this.$root.$on("superadmin-userlist:fetchUsers", async () => {
      this.users = await this.handleFetchTenantUsers(
        this.adminRole.id,
        this.tenant.id
      );
    });
  },
  beforeDestroy() {
    this.$root.$off("superadmin-userlist:fetchUsers");
  },
  methods: {
    async handleFetchTenantUsers(roleId, slug) {
      this.isLoading = true;
      const { data } = await axios.get(`/roles/${roleId}/users`, {
        params: {
          paranoid: false
        },
        headers: {
          "X-Request-Origin": `${slug}.headroomapp.com`
        }
      });

      this.isLoading = false;
      return data.data;
    },
    setAdminRole() {
      this.adminRole = this.roles.find(role => role.value === 8);
    },
    handleUserView(node, user) {
      node.stopPropagation();

      if (node.target != node.currentTarget) return;

      this.$emit("viewTenantUser", {
        user,
        tenant: this.tenant,
        roles: this.roles
      });
    },
    handleUserDelete(user) {
      this.$swal({
        text: "Confirm to delete account.",
        type: "warning",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        showCancelButton: true,
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !this.$swal.isLoading(),
        preConfirm: () => {
          return axios
            .delete(`/users/${user.id}`, {
              headers: {
                "X-Request-Origin": this.tenant.id + ".headroomapp.com"
              }
            })
            .then(res => {
              if (res.status === 204) {
                return res;
              }

              throw new Error(res.statusText);
            })
            .catch(error => {
              this.$swal.showValidationMessage(error);
            });
        }
      })
        .then(result => {
          if (result.dismiss) return;

          if (result.value.status === 204) {
            this.$root.$emit("superadmin-userlist:fetchUsers");
            this.$toastr.warning(`${user.email} has been deactivated.`);
          }
        })
        .catch(() => {
          this.$swal.showValidationMessage("Unable to handle request");
        });
    },
    handleUserRestore(user) {
      this.$swal({
        text: "Confirm to restore account.",
        type: "warning",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        showCancelButton: true,
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !this.$swal.isLoading(),
        preConfirm: () => {
          return axios
            .patch(`/users/${user.id}`, null, {
              params: {
                restore: true
              },
              headers: {
                "X-Request-Origin": this.tenant.id + ".headroomapp.com"
              }
            })
            .then(res => {
              if (res.status === 200) {
                return res;
              }

              throw new Error(res.statusText);
            })
            .catch(error => {
              this.$swal.showValidationMessage(error);
            });
        }
      })
        .then(result => {
          if (result.dismiss) return;

          if (result.value.status === 200) {
            this.$root.$emit("superadmin-userlist:fetchUsers");
            this.$toastr.success(`${user.email} has been restored.`);
          }
        })
        .catch(() => {
          this.$swal.showValidationMessage("Unable to handle request");
        });
    },
    handleUserMore() {
      logger.log("[handleUserMore]");
    }
  }
};
</script>

<style lang="scss" scoped>
.user {
  font-size: 0.875rem;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
}

.block {
  background-color: $gray-50;
  font-size: 0.875rem;

  &-title {
    border-bottom: 1px solid $gray-250;
    font-size: 0.75rem;
  }

  &-header {
    &-options {
      margin-left: 0.3125rem;
    }
  }
}

.header {
  &__option {
    background: transparent;
    border: 0;
    border-radius: 2rem;
    cursor: pointer;
    height: 2rem;
    position: relative;
    width: 2rem;

    &:before {
      background-color: $gray-200;
      border-radius: 50%;
      content: "";
      cursor: pointer;
      height: 100%;
      left: 0;
      pointer-events: none;
      position: absolute;
      overflow: hidden;
      top: 0;
      transform: scale(0);
      transition: all 200ms ease;
      width: 100%;
      z-index: -1;
    }

    &:hover {
      &:before {
        transform: scale(1);
        z-index: 1;
      }
    }

    > span {
      position: relative;
      z-index: 1;
    }
  }

  &-loading {
    font-size: 0.875rem;
  }
}
</style>
